import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import NunitoSemiBold from "../../../../../assets/fonts/Nunito/Nunito-SemiBold.ttf";
import { formatToTwoDecimal } from "../../../../../utils/numberFormatter";
import { formatPartyName } from "../../../../../utils/export/exportMeta";
import PdfExportHeader from "../../../../../components/Export/PdfExportHeader";

Font.register({
  family: "Nunito",
  src: NunitoSemiBold,
});

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 60,
  },
  invoiceBlock: {
    marginBottom: 16,
    padding: 12,
    borderWidth: 2,
    borderColor: "#403E92",
    borderStyle: "solid",
    borderRadius: 4,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  infoLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 11,
    color: "#403E92",
    width: 120,
  },
  infoValue: {
    fontSize: 11,
    fontFamily: "Nunito",
    flexGrow: 1,
    flexBasis: 0,
    paddingRight: 20,
  },
  itemListLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    color: "#403E92",
    marginTop: 8,
    marginBottom: 6,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    minHeight: 24,
  },
  itemColHeader: {
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#403E92",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 6,
    textAlign: "center",
    backgroundColor: "#403E92",
    color: "white",
  },
  itemCol: {
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 6,
    textAlign: "center",
  },
  tableCell: {
    fontSize: 9,
    fontFamily: "Nunito",
  },
  noData: {
    fontSize: 12,
    textAlign: "center",
    paddingVertical: 12,
  },
  footer: {
    position: "absolute",
    bottom: -20,
    left: 0,
    right: 0,
    textAlign: "right",
    fontSize: 10,
  },
});

const ITEM_COLUMNS = [
  "Product Code",
  "Product Name",
  "Unit Price",
  "Qty",
  "Moisture",
  "Net Qty",
  "Discount",
  "Subtotal",
];

// Company name first; otherwise first name, plus last name when there is one
export function getCustomerName(customer) {
  if (!customer) return "";
  return formatPartyName(
    customer.company_name,
    customer.first_name,
    customer.last_name,
  );
}

function getCurrencySymbol(currency_name) {
  if (currency_name === "USD") return "$";
  if (currency_name === "PHP") return "₱";
  return currency_name || "₱";
}

function formatWithCurrency(value, currency_name) {
  if (value === undefined || value === null) return "";
  return getCurrencySymbol(currency_name) + formatToTwoDecimal(value);
}

// `records` are already-loaded invoices (see invoiceExportConfig) and `meta`
// comes from buildExportMeta. This file must not fetch, since it is rendered
// inside invoicePdf.worker.jsx.
const InvoicePdf = ({ records = [], meta }) => (
  <Document title={meta.fileName}>
    <Page size="A4" style={styles.page} orientation="landscape">
        <PdfExportHeader meta={meta} />

        {records.length > 0 ? (
          records.map((invoice, idx) => (
            <View
              key={invoice.sales_invoice_id || idx}
              style={styles.invoiceBlock}
              wrap={false}
            >
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Transaction Number</Text>
                <Text style={styles.infoValue}>
                  {invoice.client_transaction_id || ""}
                </Text>
                <Text style={styles.infoLabel}>Invoice Date</Text>
                <Text style={styles.infoValue}>
                  {invoice.invoice_date ? String(invoice.invoice_date) : ""}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Customer Name</Text>
                <Text style={styles.infoValue}>
                  {getCustomerName(invoice.customer)}
                </Text>
                <Text style={styles.infoLabel}>Due Date</Text>
                <Text style={styles.infoValue}>
                  {invoice.due_date ? String(invoice.due_date) : ""}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>DR Number</Text>
                <Text style={styles.infoValue}>{invoice.dr_number || ""}</Text>
                <Text style={styles.infoLabel}>Total Amount</Text>
                <Text style={styles.infoValue}>
                  {invoice.total_amount !== undefined
                    ? formatWithCurrency(
                        invoice.total_amount,
                        invoice.currency_name,
                      )
                    : ""}
                </Text>
              </View>

              <Text style={styles.itemListLabel}>Item List</Text>
              <View style={styles.table}>
                <View style={styles.tableRow} fixed>
                  {ITEM_COLUMNS.map((col) => (
                    <View key={col} style={styles.itemColHeader}>
                      <Text style={styles.tableCell}>{col}</Text>
                    </View>
                  ))}
                </View>
                {invoice.items && invoice.items.length > 0 ? (
                  invoice.items.map((item, i) => (
                    <View
                      key={item.stock_management_id || i}
                      style={styles.tableRow}
                      wrap={false}
                    >
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.product_code || ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.product_name || ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.unit_price !== undefined
                            ? formatWithCurrency(
                                item.unit_price,
                                invoice.currency_name,
                              )
                            : ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.quantity ?? ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.moisture ?? ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.net_weight ?? ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.discount_item ?? ""}
                        </Text>
                      </View>
                      <View style={styles.itemCol}>
                        <Text style={styles.tableCell}>
                          {item.subtotal !== undefined
                            ? formatWithCurrency(
                                item.subtotal,
                                invoice.currency_name,
                              )
                            : ""}
                        </Text>
                      </View>
                    </View>
                  ))
                ) : (
                  <View style={styles.tableRow}>
                    <View style={[styles.itemCol, { width: "100%" }]}>
                      <Text style={styles.tableCell}>No items</Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          ))
        ) : (
          <Text style={styles.noData}>No data</Text>
        )}

        <View fixed>
          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
    </Page>
  </Document>
);

export default InvoicePdf;
